/**
 * Role-gated primary nav (the left rail).
 *
 * The rail lists the top-level MODULES (Chat / CRM / Hotel Supplier / Expense).
 * The rule is now one sentence: a module appears when the user can reach at
 * least one PAGE inside it.
 *
 * That rule lives in the page registry (features/admin/lib/page-registry.ts),
 * which is also what the admin User Management screen reads. Before, the rail
 * kept its own SURFACE_BY_PATH + AUTHORITY_BY_PATH maps and the admin screen had
 * no way to know about them — so "what can this person see" had two answers.
 * Now there is one, and adding a page updates the rail, the module tab bar, and
 * the admin display together.
 *
 * A rail item whose path isn't in the registry (e.g. the Admin item the shell
 * appends) is left ALONE — this function only filters what it knows about.
 *
 * UI affordance only, NOT the security boundary — each surface enforces
 * server-side (CRM is deny-by-default in cm-api). Admins (server-verified) and
 * the fail-open case (roles claim unavailable) see every module — except an
 * EXPLICIT-GRANT-ONLY item (EXPLICIT_GRANT_ONLY_NAV, e.g. Commissions), which
 * needs the authority itself and is hidden from an admin who lacks the grant.
 */
import type { NavItem } from '@andavo/web-ui';
import {
  APP_PAGES,
  pagesForRoles,
  type PageModule,
} from '@/features/admin/lib/page-registry';
import { deriveAuthorityKeys } from '@/features/admin/lib/rbac-model';

function pathKey(path: NavItem['path']): string {
  return typeof path === 'string' ? path : (path.pathname ?? '');
}

/** First path segment, e.g. '/crm/customers' → 'crm'. */
function rootSegment(path: string): string {
  return path.split('/').filter(Boolean)[0] ?? '';
}

/**
 * Which module does this rail path represent? Derived from the registry by
 * matching the rail item's root segment against the pages' root segments, so a
 * rail path of '/crm' or '/crm/home' both resolve to the CRM module.
 */
const MODULE_BY_ROOT: Map<string, PageModule> = new Map(
  APP_PAGES.map((p) => [rootSegment(p.path), p.module]),
);

function moduleForNavPath(path: string): PageModule | undefined {
  return MODULE_BY_ROOT.get(rootSegment(path));
}

/**
 * EXPLICIT-GRANT-ONLY rail items: rail root segment → the authority the user's
 * stored roles must DERIVE for the item to show.
 *
 * These are the one exception to "admins see every module". The authority is
 * explicit-grant-only in the RBAC model (the admin role deliberately lacks it),
 * so the admin bypass and the fail-open branch below do NOT apply: the item
 * shows only when a held role confers the authority, and hides when the roles
 * are unknown ("can't tell" is not a grant).
 *
 * '/commissions' is reserved now, before its rail item exists, so the item is
 * gated the moment someone adds it to platformNav — nobody has to remember.
 */
export const EXPLICIT_GRANT_ONLY_NAV: ReadonlyMap<string, string> = new Map([
  ['commissions', 'review:commissions'],
]);

/**
 * Filter the platform rail to the modules a user should see. Admins and the
 * fail-open (unknown roles claim) case return the items unchanged — except
 * explicit-grant-only items, which are always decided by the derived authority
 * set and fail CLOSED.
 */
export function gatePlatformNav(
  items: NavItem[],
  storedRoles: readonly string[] | undefined,
  isAdmin: boolean,
  explicitGrantOnly: ReadonlyMap<string, string> = EXPLICIT_GRANT_ONLY_NAV,
): NavItem[] {
  // Runs BEFORE the admin bypass and the fail-open branch, on purpose.
  const held = new Set(
    storedRoles === undefined ? [] : deriveAuthorityKeys(storedRoles),
  );
  const granted = items.filter((item) => {
    const required = explicitGrantOnly.get(rootSegment(pathKey(item.path)));
    return required === undefined || held.has(required);
  });

  if (isAdmin) return granted; // platform admin → every (ordinary) module
  if (storedRoles === undefined) return granted; // roles claim unavailable → fail open

  // The modules the user can reach at least one page in.
  const reachable = new Set<PageModule>(
    pagesForRoles(storedRoles).map((p) => p.module),
  );

  return granted.filter((item) => {
    const module = moduleForNavPath(pathKey(item.path));
    // Not a registry-managed module (e.g. the shell's Admin item) → untouched.
    if (module === undefined) return true;
    return reachable.has(module);
  });
}
